/**
 * Grounding + IR assembly, shared by every parser. A parser's only job is to
 * produce a `PatternMatch` (the deterministic parser does it with regex
 * templates; the LLM parser does it by prompting a model). From there, grounding
 * mentions → IRIs and building the `QueryIR` is identical — so it lives here once.
 */
import type { QueryIR, TriplePattern } from "../ir/types.ts";
import { iri, triple, v } from "../ir/build.ts";
import type {
  EntityLinker,
  PropertyCandidate,
  RelationLinker,
} from "../linking/types.ts";
import { lookupClass, lookupSuperlative } from "../linking/lexicon.ts";
import {
  WIKIDATA_DIRECT_PREFIX,
  WIKIDATA_ENTITY_PREFIX,
} from "../linking/wikidata.ts";
import type {
  LinkedEntity,
  LinkedRelation,
  ParseOverrides,
  ParseResult,
  ParseTrace,
} from "../parser/types.ts";
import { ParseError } from "../parser/types.ts";
import type { PatternMatch } from "./patterns.ts";

const P31_INSTANCE_OF = WIKIDATA_DIRECT_PREFIX + "P31";

export interface GroundOptions {
  entityLinker: EntityLinker;
  relationLinker: RelationLinker;
  sourceId: string;
  parserName: string;
  question: string;
  signal?: AbortSignal;
  overrides?: ParseOverrides;
}

/** Pin a chosen IRI to the front of the candidate list (synthesizing it if absent). */
function pinEntity(
  candidates: import("../linking/types.ts").EntityCandidate[],
  pin: string,
  label: string | undefined,
  sourceId: string,
): import("../linking/types.ts").EntityCandidate[] {
  const found = candidates.find((c) => c.iri === pin);
  if (found) return [found, ...candidates.filter((c) => c.iri !== pin)];
  return [{ iri: pin, label: label ?? pin, score: 1, sourceId }, ...candidates];
}

function pinProperty(
  candidates: PropertyCandidate[],
  pin: string,
  label: string | undefined,
  sourceId: string,
): PropertyCandidate[] {
  const found = candidates.find((c) => c.iri === pin);
  if (found) return [found, ...candidates.filter((c) => c.iri !== pin)];
  const idMatch = pin.match(/\/(P\d+)$/);
  const synth: PropertyCandidate = { iri: pin, label: label ?? pin, score: 1, sourceId, origin: "lexicon" };
  if (idMatch?.[1]) {
    synth.id = idMatch[1];
    synth.entityIri = WIKIDATA_ENTITY_PREFIX + idMatch[1];
  }
  return [synth, ...candidates];
}

export async function groundAndBuild(
  match: PatternMatch,
  opts: GroundOptions,
): Promise<ParseResult> {
  const trace: ParseTrace = {
    question: opts.question,
    normalized: opts.question.trim(),
    parser: opts.parserName,
    template: match.template,
    form: match.form,
    entities: [],
    relations: [],
    notes: [],
  };

  // Ground the entity mention (honoring a disambiguation pin, if any).
  let entity: LinkedEntity | undefined;
  if (match.entityMention) {
    let candidates = await opts.entityLinker.link(match.entityMention, { signal: opts.signal });
    if (opts.overrides?.entityIri) {
      candidates = pinEntity(candidates, opts.overrides.entityIri, opts.overrides.entityLabel, opts.sourceId);
    }
    const chosen = candidates[0];
    entity = { mention: match.entityMention, alternatives: candidates, ...(chosen ? { chosen } : {}) };
    trace.entities.push(entity);
    if (!chosen) throw new ParseError(`Couldn't find an entity matching "${match.entityMention}".`, trace);
  }

  // Ground the relation (honoring a disambiguation pin, if any).
  let relation: LinkedRelation | undefined;
  if (match.relationPhrase) {
    let candidates = await opts.relationLinker.link(match.relationPhrase, { signal: opts.signal });
    if (opts.overrides?.propertyIri) {
      candidates = pinProperty(candidates, opts.overrides.propertyIri, opts.overrides.propertyLabel, opts.sourceId);
    }
    const chosen = candidates[0];
    relation = { phrase: match.relationPhrase, alternatives: candidates, ...(chosen ? { chosen } : {}) };
    trace.relations.push(relation);
    if (!chosen) throw new ParseError(`Couldn't find a property matching "${match.relationPhrase}".`, trace);
  }

  // Resolve a class noun via the curated lexicon (best-effort type constraint).
  // The lexicon (wd: Q-ids + wdt:P31) is Wikidata-specific, so only apply it for
  // the Wikidata source — injecting wd: IRIs into a local/other query is wrong.
  let classConstraint: ParseTrace["classConstraint"];
  if (match.classNoun && opts.sourceId === "wikidata") {
    const cls = lookupClass(match.classNoun);
    if (cls) {
      classConstraint = { noun: match.classNoun, qid: cls.qid, label: cls.label };
      trace.classConstraint = classConstraint;
    } else {
      trace.notes.push(`No known class for "${match.classNoun}" — omitting the type filter.`);
    }
  } else if (match.classNoun) {
    trace.notes.push(`Class filtering isn't wired for this source yet — omitting the "${match.classNoun}" type filter.`);
  }

  const ir = buildIr(match, {
    sourceId: opts.sourceId,
    entityIri: entity?.chosen?.iri,
    entityLabel: entity?.chosen?.label,
    relation: relation?.chosen,
    classConstraint,
    trace,
  });

  return { ir, trace };
}

interface BuildContext {
  sourceId: string;
  entityIri?: string;
  entityLabel?: string;
  relation?: PropertyCandidate;
  classConstraint?: ParseTrace["classConstraint"];
  trace: ParseTrace;
}

function buildIr(match: PatternMatch, ctx: BuildContext): QueryIR {
  const { entityIri, entityLabel, relation, classConstraint, trace } = ctx;
  const src = [ctx.sourceId];

  const classPattern = (subjectVar: string): TriplePattern | null =>
    classConstraint
      ? triple(
          v(subjectVar),
          iri(P31_INSTANCE_OF, "instance of"),
          iri(WIKIDATA_ENTITY_PREFIX + classConstraint.qid, classConstraint.label),
          { sourceIds: src },
        )
      : null;

  switch (match.kind) {
    case "attribute-of":
    case "possessive":
    case "active-who": {
      assertGround(entityIri && relation, "entity + relation required", trace);
      return {
        form: "select",
        distinct: true,
        projection: ["answer"],
        focusVar: "answer",
        wantLabels: true,
        filters: [],
        patterns: [
          triple(iri(entityIri!, entityLabel), iri(relation!.iri, relation!.label), v("answer"), {
            sourceIds: src,
          }),
        ],
      };
    }

    case "relation-list": {
      assertGround(entityIri && relation, "entity + relation required", trace);
      const patterns: TriplePattern[] = [
        triple(v("item"), iri(relation!.iri, relation!.label), iri(entityIri!, entityLabel), {
          sourceIds: src,
        }),
      ];
      const cp = classPattern("item");
      if (cp) patterns.push(cp);
      return {
        form: "select",
        distinct: true,
        projection: ["item"],
        focusVar: "item",
        wantLabels: true,
        filters: [],
        patterns,
      };
    }

    case "count-of": {
      assertGround(entityIri && relation, "entity + relation required", trace);
      return {
        form: "count",
        distinct: true,
        projection: ["item"],
        focusVar: "item",
        filters: [],
        patterns: [
          triple(iri(entityIri!, entityLabel), iri(relation!.iri, relation!.label), v("item"), {
            sourceIds: src,
          }),
        ],
      };
    }

    case "count-class": {
      if (!classConstraint) {
        throw new ParseError(
          `I don't have a class mapping for "${match.classNoun}" yet, so I can't count them.`,
          trace,
        );
      }
      return {
        form: "count",
        distinct: true,
        projection: ["item"],
        focusVar: "item",
        filters: [],
        patterns: [classPattern("item")!],
      };
    }

    case "superlative": {
      if (!classConstraint) {
        throw new ParseError(
          `I don't have a class mapping for "${match.classNoun}" yet, so I can't rank them.`,
          trace,
        );
      }
      const sup = lookupSuperlative(match.superlativeAdj ?? "");
      if (!sup) throw new ParseError(`Unknown superlative "${match.superlativeAdj}".`, trace);
      trace.superlative = {
        adjective: match.superlativeAdj ?? "",
        pid: sup.pid,
        label: sup.label,
        descending: sup.descending,
      };
      return {
        form: "select",
        distinct: false,
        projection: ["item", "value"],
        focusVar: "item",
        wantLabels: true,
        filters: [],
        orderBy: { variable: "value", descending: sup.descending },
        limit: 1,
        patterns: [
          classPattern("item")!,
          triple(v("item"), iri(WIKIDATA_DIRECT_PREFIX + sup.pid, sup.label), v("value"), {
            sourceIds: src,
          }),
        ],
      };
    }
  }
}

function assertGround(cond: unknown, message: string, trace: ParseTrace): asserts cond {
  if (!cond) throw new ParseError(`Internal parser error: ${message}`, trace);
}
